import './index.css';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import classnames from 'classnames';
import { throttle } from '@hh.ru/magritte-common-func-utils';
import { keyboardMatches, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { useResize } from '@hh.ru/magritte-common-resize';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { Action } from '@hh.ru/magritte-ui-action';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { CrossOutlinedSize24, ChevronDownOutlinedSize24, CrossOutlinedSize16, ChevronDownOutlinedSize16 } from '@hh.ru/magritte-ui-icon/icon';

var styles = {"trigger-root":"magritte-trigger-root___dPhW4_3-0-31","triggerRoot":"magritte-trigger-root___dPhW4_3-0-31","disabled":"magritte-disabled___ZTuRA_3-0-31","trigger-root_stretched":"magritte-trigger-root_stretched___IhAsT_3-0-31","triggerRootStretched":"magritte-trigger-root_stretched___IhAsT_3-0-31","trigger":"magritte-trigger___IpHhR_3-0-31","xsmall":"magritte-xsmall___8iEkS_3-0-31","applied":"magritte-applied___jrfWs_3-0-31","content":"magritte-content___5eg6m_3-0-31","label_elevated":"magritte-label_elevated___N0xnk_3-0-31","labelElevated":"magritte-label_elevated___N0xnk_3-0-31","postfix":"magritte-postfix___u4999_3-0-31","small":"magritte-small___DphY3_3-0-31","medium":"magritte-medium___0kuIk_3-0-31","large":"magritte-large___1eRM6_3-0-31","invalid":"magritte-invalid___Xlbyg_3-0-31","icon":"magritte-icon___HbC0r_3-0-31","selected":"magritte-selected___NjQNn_3-0-31","content_medium":"magritte-content_medium___VnlIy_3-0-31","contentMedium":"magritte-content_medium___VnlIy_3-0-31","content_large":"magritte-content_large___byZTA_3-0-31","contentLarge":"magritte-content_large___byZTA_3-0-31","content_label-can-be-elevated":"magritte-content_label-can-be-elevated___HRkpl_3-0-31","contentLabelCanBeElevated":"magritte-content_label-can-be-elevated___HRkpl_3-0-31","values-wrapper":"magritte-values-wrapper___yQbdl_3-0-31","valuesWrapper":"magritte-values-wrapper___yQbdl_3-0-31","values-wrapper_label-can-elevated":"magritte-values-wrapper_label-can-elevated___A0bzp_3-0-31","valuesWrapperLabelCanElevated":"magritte-values-wrapper_label-can-elevated___A0bzp_3-0-31","values":"magritte-values___6wj-v_3-0-31","label":"magritte-label___VsUEF_3-0-31","icon_expanded":"magritte-icon_expanded___ggr1u_3-0-31","iconExpanded":"magritte-icon_expanded___ggr1u_3-0-31","postfix_metric":"magritte-postfix_metric___jO3qH_3-0-31","postfixMetric":"magritte-postfix_metric___jO3qH_3-0-31"};

// расчитываем размер шеврона на базе `size`
// если состояние `applied`, то нужно использовать крест
const getIcon = (size, applied) => {
    if (size === 'large' || size === 'medium') {
        if (applied) {
            return CrossOutlinedSize24;
        }
        return ChevronDownOutlinedSize24;
    }
    if (applied) {
        return CrossOutlinedSize16;
    }
    return ChevronDownOutlinedSize16;
};
const defaultRenderFormattedValue = (value, index, length) => {
    return (jsxs(Fragment, { children: [value, index !== length - 1 && ', '] }));
};
const TriggerComponent = ({ applied, description, disabled, expanded, maxWidth, invalid, values = [], label, elevateLabel, errorMessage, renderFormattedValue = defaultRenderFormattedValue, trls, stretched, size, showPostfix, onChange, onClear, onFocus, onKeyDown, onBlur, 'data-qa': dataQa, }, ref) => {
    const triggerRef = useRef(null);
    const triggerRefCallback = useMultipleRefs(ref, triggerRef);
    const itemsRefs = useRef([]);
    const itemsWrapperRef = useRef(null);
    const postfixMetricRef = useRef(null);
    const showValues = values.length > 0;
    const labelCanBeElevated = size === 'large' && !!elevateLabel && !!label;
    const labelIsElevated = showValues && labelCanBeElevated;
    const IconComponent = getIcon(size, Boolean(applied));
    const [postfix, setPostfix] = useState(0);
    const postfixRef = useRef(postfix);
    postfixRef.current = postfix;
    const formattedValues = useMemo(() => {
        itemsRefs.current = [];
        return values.map((value, index) => (jsx("span", { ref: (element) => element && (itemsRefs.current[index] = element), children: renderFormattedValue(value, index, values.length) }, index)));
    }, [renderFormattedValue, values]);
    const handleClickTrigger = useCallback(() => {
        onChange(!expanded);
    }, [onChange, expanded]);
    const handleKeyDownTrigger = useCallback((event) => {
        if (keyboardMatches(event.nativeEvent, [keyboardKeys.Space])) {
            onChange(!expanded);
        }
        onKeyDown?.(event);
    }, [onChange, onKeyDown, expanded]);
    const handleMouseDownClear = useCallback((event) => {
        onClear?.();
        event.preventDefault();
    }, [onClear]);
    const handleKeyDownClear = useCallback((event) => {
        if (keyboardMatches(event.nativeEvent, [keyboardKeys.Space])) {
            onClear?.();
            triggerRef.current?.focus();
            event.stopPropagation();
        }
    }, [onClear]);
    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const calcPostfix = useCallback(throttle(() => {
        if (!itemsWrapperRef.current || !postfixMetricRef.current) {
            return;
        }
        if (!showPostfix) {
            return;
        }
        // перевод в постфиксе может поменяться, а ширину надо учесть
        postfixMetricRef.current.style.display = 'inline';
        const postfixRect = postfixMetricRef.current.getBoundingClientRect();
        const postfixWidth = postfixRect.width;
        postfixMetricRef.current.style.display = 'none';
        const parentRect = itemsWrapperRef.current.getBoundingClientRect();
        const parentWidth = parentRect.width;
        // если постфикс виден, то ширину его не учитываем (реф стоит на values)
        const parentWidthWithoutPostfix = parentWidth - (postfixRef.current === 0 ? postfixWidth : 0);
        // ищем первый элемент, где значения схлопываются в ...
        let truncatedItemIndex = -1;
        let childrenWidth = 0;
        itemsRefs.current.forEach((itemRef, index) => {
            const itemRect = itemRef.getBoundingClientRect();
            childrenWidth += itemRect.width;
            if (truncatedItemIndex === -1 && childrenWidth > parentWidthWithoutPostfix) {
                truncatedItemIndex = index;
            }
        });
        // проверяем, есть ли ... в тригере
        if (Math.abs(parentWidth - childrenWidth) < 1) {
            setPostfix(0);
            return;
        }
        const length = values.length - 1;
        if (truncatedItemIndex === -1 || truncatedItemIndex === length) {
            setPostfix(0);
            return;
        }
        setPostfix(length - truncatedItemIndex);
    }, 50), [values, showPostfix, maxWidth]);
    useLayoutEffect(calcPostfix, [calcPostfix]);
    useResize(calcPostfix);
    return (jsxs("div", { className: classnames(styles.triggerRoot, { [styles.triggerRootStretched]: stretched }), style: maxWidth ? { maxWidth } : {}, children: [jsxs("div", { ref: triggerRefCallback, className: classnames(styles.trigger, styles[size], {
                    [styles.applied]: applied,
                    [styles.disabled]: disabled,
                    [styles.invalid]: invalid,
                    [styles.selected]: showValues,
                }), onClick: !disabled ? handleClickTrigger : undefined, onKeyDown: !disabled ? handleKeyDownTrigger : undefined, onFocus: onFocus, onBlur: onBlur, tabIndex: disabled ? -1 : 0, "data-qa": dataQa || 'trigger-root', children: [jsxs("div", { className: classnames(styles.content, styles[`content_${size}`], {
                            [styles.contentLabelCanBeElevated]: labelCanBeElevated,
                        }), children: [labelCanBeElevated && (jsx("div", { className: classnames(styles.label, {
                                    [styles.labelElevated]: labelIsElevated,
                                }), children: label })), !labelCanBeElevated && !showValues && jsx("div", { className: styles.label, children: label }), showValues && (jsxs("div", { className: classnames(styles.valuesWrapper, {
                                    [styles.valuesWrapperLabelCanElevated]: labelIsElevated,
                                }), children: [jsx("div", { ref: itemsWrapperRef, className: styles.values, "data-qa": "trigger-values-wrapper", children: formattedValues }), showPostfix && postfix > 0 && (jsx("div", { className: styles.postfix, "data-qa": "trigger-postfix", children: `${trls?.postfix} ${postfix}` }))] })), showPostfix && (jsx("span", { ref: postfixMetricRef, className: classnames(styles.postfix, styles.postfixMetric), "data-qa": "trigger-postfix-metric", children: `${trls?.postfix} ${postfix || 1}` }))] }), !applied && (jsx("div", { className: classnames(styles.icon, {
                            [styles.iconExpanded]: expanded,
                        }), children: jsx(IconComponent, {}) })), applied && (jsx("div", { className: styles.icon, children: jsx(Action, { onClick: () => null, onKeyDown: handleKeyDownClear, onMouseDown: handleMouseDownClear, icon: IconComponent, disabled: disabled, disablePadding: true }) }))] }), jsx(FormHelper, { description: description, errorMessage: errorMessage, disabled: disabled, invalid: invalid })] }));
};
const Trigger = forwardRef(TriggerComponent);

export { Trigger, TriggerComponent };
//# sourceMappingURL=Trigger.js.map
